import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import { api, formatApiError } from "@/lib/api";
import { FadeUp } from "@/components/Reveal";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#121210] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-emerald-500/50 focus:shadow-[0_0_15px_rgba(16,185,129,0.1)] transition-all duration-200";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      setSuccess(true);
      setTimeout(() => navigate("/auth"), 3000);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <main data-testid="reset-password-page" className="pt-28 md:pt-16 min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-red-400">Invalid reset link.</p>
          <Link to="/forgot-password" className="text-emerald-400 hover:text-emerald-300 text-sm">
            Request a new one
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="reset-password-page" className="pt-28 md:pt-16 min-h-screen flex items-center justify-center relative overflow-hidden">
      <Seo title="Reset password — HighRank" siteName="HighRank" description="Set a new password for your HighRank account." />
      <div className="absolute inset-0 bg-grid opacity-30" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[30rem] h-[18rem] rounded-full bg-emerald-500/10 blur-[110px]" />

      <FadeUp className="relative w-full max-w-md px-4 py-10">
        <div className="text-center mb-8">
          <Link to="/" data-testid="reset-logo" className="inline-flex mb-6" aria-label="HighRank.space home">
            <Logo />
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-wide text-slate-100">
            New password
          </h1>
          <p className="mt-2 text-sm text-slate-500">Choose a strong password for your account.</p>
        </div>

        {success ? (
          <div
            data-testid="reset-password-success"
            className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-7 text-center space-y-4"
          >
            <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mx-auto">
              <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-slate-200 font-medium">Password updated!</p>
            <p className="text-sm text-slate-500">Redirecting you to sign in…</p>
            <Link
              to="/auth"
              data-testid="reset-go-to-login"
              className="inline-block mt-2 text-sm text-emerald-400 hover:text-emerald-300 transition-colors"
            >
              Sign in now
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-password-form" className="rounded-2xl border border-white/[0.07] bg-[#0A0A09] p-7 space-y-4">
            <div>
              <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">New password</label>
              <input
                data-testid="reset-password-input"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                required
                placeholder="At least 8 characters"
                className={inputCls}
              />
            </div>
            <div>
              <label className="block font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Confirm password</label>
              <input
                data-testid="reset-confirm-input"
                type="password"
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                required
                placeholder="Re-enter your password"
                className={inputCls}
              />
            </div>
            {error && (
              <p data-testid="reset-password-error" className="text-sm text-red-400">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="reset-password-submit"
              disabled={submitting}
              className="w-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-black font-bold rounded-full py-3 transition-all duration-200 hover:shadow-[0_0_25px_rgba(16,185,129,0.4)]"
            >
              {submitting ? "Saving…" : "Set new password"}
            </button>
          </form>
        )}
      </FadeUp>
    </main>
  );
}
